import React, { useState, useEffect } from "react";
import "@cloudscape-design/global-styles/index.css";
import { Spinner, Box } from "@cloudscape-design/components";
import { AppShell, ErrorBoundary } from "./Components";
import { SearchPage, KeyMasterPage, RedrivePage } from "./pages";
import { ApiClient } from "./services";
import { useNotifications } from "./hooks";
import { Stage, Route, NAV } from "./constants";

const getRoute = (): Route => {
  const hash = window.location.hash.split("?")[0];
  if (hash.startsWith(NAV.ROUTES.KEYMASTER)) {
    return "keymaster";
  }
  if (hash.startsWith(NAV.ROUTES.REDRIVE)) {
    return "redrive";
  }
  return "search";
};

export const App: React.FC = () => {
  const [stage, setStage] = useState<Stage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [route, setRoute] = useState<Route>(getRoute);
  const { items, notify } = useNotifications();

  useEffect(() => {
    ApiClient.getInstance()
      .init()
      .then(() => {
        setStage(ApiClient.getInstance().getStage());
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Unknown error");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleHashChange = (): void => setRoute(getRoute());
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (error || !stage) {
    return (
      <Box textAlign="center" color="text-status-error" padding="xxl">
        Failed to initialize: {error}
      </Box>
    );
  }

  const renderPage = (): React.ReactNode => {
    switch (route) {
      case "keymaster":
        return <KeyMasterPage stage={stage} onNotify={notify} />;
      case "redrive":
        return <RedrivePage stage={stage} onNotify={notify} />;
      default:
        return <SearchPage onNotify={notify} />;
    }
  };

  return (
    <AppShell stage={stage} notifications={items} activeRoute={route}>
      <ErrorBoundary key={route}>{renderPage()}</ErrorBoundary>
    </AppShell>
  );
};
